import { AlertCircle, Archive, Info, LogOut, Trash2 } from "lucide-react";
import { useImperativeHandle, useRef, useState } from "react";
import EnterButton from "@/components/buttons/EnterButton";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertDialogType } from "../types/alert-dialog";

const getIcon = (type: AlertDialogType) => {
    switch (type) {
        case AlertDialogType.Simple:
            return <Info className="size-6 text-blue-500" />;
        case AlertDialogType.Archive:
            return <Archive className="size-4 text-orange-500" />;
        case AlertDialogType.Unarchive:
            return <Archive className="size-4 text-green-500" />;
        case AlertDialogType.Confirm:
            return <AlertCircle className="size-4 text-orange-500" />;
        case AlertDialogType.Delete:
            return <Trash2 className="size-4 text-red-500" />;
        case AlertDialogType.Leave:
            return <LogOut className="size-4 text-orange-500" />;
    }
};

export interface AlertDialogImperativeHandle {
    show: (overrides?: { title?: string; description?: string }) => Promise<boolean>;
}

export function DefaultAlertDialog({
    ref,
    type = AlertDialogType.Simple,
    title,
    description,
}: {
    ref: React.RefObject<AlertDialogImperativeHandle | null>;
    type: AlertDialogType;
    title: string;
    description: string;
}) {
    const [open, setOpen] = useState(false);
    const [overrides, setOverrides] = useState<{ title?: string; description?: string }>({});
    const resolveRef = useRef<((value: boolean) => void) | null>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => ({
        show: (overrides) => {
            setOverrides(overrides ?? {});
            setOpen(true);
            return new Promise<boolean>((resolve) => {
                resolveRef.current = resolve;
            });
        },
    }));

    const handleCancel = () => {
        // resolve the promise with false
        if (resolveRef.current) {
            resolveRef.current(false);
            resolveRef.current = null;
        }
        setOpen(false);
    };

    const handleConfirm = () => {
        // resolve the promise with true
        if (resolveRef.current) {
            resolveRef.current(true);
            resolveRef.current = null;
        }
        setOpen(false);
    };

    return (
        <AlertDialog open={open} onOpenChange={(open) => !open && handleCancel()}>
            <AlertDialogContent initialFocus={submitButtonRef} className="gap-6 rounded-md">
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 leading-4 mb-2">
                        {getIcon(type)}
                        {overrides.title ?? title}
                    </AlertDialogTitle>
                    <AlertDialogDescription>{overrides.description ?? description}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    {type !== AlertDialogType.Simple && (
                        <AlertDialogCancel
                            render={
                                <Button type="button" variant="outline" size="sm">
                                    Cancel
                                </Button>
                            }
                            onClick={handleCancel}
                        ></AlertDialogCancel>
                    )}
                    <AlertDialogAction
                        size="sm"
                        render={
                            <EnterButton
                                nativeButton={true}
                                isSubmitting={false}
                                disabled={false}
                                ref={submitButtonRef}
                            >
                                Confirm
                            </EnterButton>
                        }
                        onClick={handleConfirm}
                    ></AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
